import { CALL_LIMITS, type CallKind } from './enums/calls';
import type { ConversationKind } from './enums';
import type { UserKind } from './enums';

/**
 * Pure call rules that the API, the worker and the clients share (PLAN §5 "Calls and huddles",
 * D-026, D-094). No I/O and no clock. The state machine itself is `@orbit/domain/calls`.
 */

// ---------------------------------------------------------------- where a call may happen

export interface ConversationCallPolicy {
  /** Whether a call or huddle may happen in a conversation of this kind at all. */
  allowed: boolean;
  /** DMs and group DMs ring the other members; channels announce with a join card instead. */
  rings: boolean;
  /** A channel and a DM may each hold one live huddle. */
  huddles: boolean;
}

/**
 * Calls in `dm` and `group_dm` ring; `public_channel` and `private_channel` announce; a `self_dm`
 * (notes to self) and a `task` comment stream never host a call (PLAN §3.1 `calls.conversation_id`).
 */
export function conversationCallPolicy(kind: ConversationKind): ConversationCallPolicy {
  switch (kind) {
    case 'dm':
    case 'group_dm':
      return { allowed: true, rings: true, huddles: true };
    case 'public_channel':
    case 'private_channel':
      return { allowed: true, rings: false, huddles: true };
    case 'self_dm':
    case 'task':
      return { allowed: false, rings: false, huddles: false };
  }
}

/**
 * Whether the person on the other side of a DM can be called at all (D-1191).
 *
 * `conversationCallPolicy` answers by conversation KIND, and a DM with the product itself — the
 * Orbit AI assistant, a bot, the system user that posts digests — is an ordinary `dm`. So the
 * header offered a phone and a camera for ringing a piece of software, next to a huddle button and
 * a bell for muting something that never notifies you. Offering a control that cannot work is
 * worse than not having it: people press it once and trust the rest a little less.
 *
 * A DM is callable when the other side is a HUMAN. Channels and group DMs are unaffected: they are
 * rooms, and a bot sitting in one does not stop the people in it talking.
 */
export function dmPartnerIsCallable(partnerKind: UserKind | null | undefined): boolean {
  return partnerKind === 'human';
}

/** Whether a 1:1 call (a `dm`) ends when either side leaves (PLAN §5 `POST /calls/:id/leave`). */
export function endsWhenEitherLeaves(kind: ConversationKind): boolean {
  return kind === 'dm';
}

// ---------------------------------------------------------------- LiveKit names (D-026)

/**
 * LiveKit room names: `ws_{workspaceId}__conv_{conversationId}__call_{callId}` for every call and
 * every huddle (D-660, amending D-026). The call id makes each instance's room unique: LiveKit
 * delivers webhooks late and at least once, and a huddle's room used to be named only by its
 * conversation, so a late `room_finished` of the huddle that just ended could end the next one. The
 * workspace is part of the name so a room can never be joined across tenants.
 *
 * `kind` is accepted for the callers written before D-660 and no longer changes the name.
 */
export function liveKitRoomName(input: {
  workspaceId: string;
  conversationId: string;
  callId: string;
  kind?: CallKind;
}): string {
  return `ws_${input.workspaceId}__conv_${input.conversationId}__call_${input.callId}`;
}

const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const ROOM_PATTERN = new RegExp(`^ws_(${UUID})__conv_(${UUID})(?:__call_(${UUID}))?$`);

export interface ParsedRoomName {
  workspaceId: string;
  conversationId: string;
  /**
   * Null only for a huddle room made before D-660 (`ws_…__conv_…`, no call part): the call is then
   * the live huddle whose `calls.livekit_room` is that exact name.
   */
  callId: string | null;
}

/** The parts of a room name made by {@link liveKitRoomName}, or null for any other name. */
export function parseLiveKitRoomName(room: string): ParsedRoomName | null {
  const match = ROOM_PATTERN.exec(room);
  if (!match) return null;
  return {
    workspaceId: match[1] as string,
    conversationId: match[2] as string,
    callId: match[3] ?? null,
  };
}

/** LiveKit participant identity: `u_{userId}_{sessionId}`, one per signed-in device (PLAN §5). */
export function liveKitIdentity(userId: string, sessionId: string): string {
  return `u_${userId}_${sessionId}`;
}

const IDENTITY_PATTERN = new RegExp(`^u_(${UUID})_(${UUID})$`);

/** The user and session of an identity made by {@link liveKitIdentity}, or null. */
export function parseLiveKitIdentity(
  identity: string,
): { userId: string; sessionId: string } | null {
  const match = IDENTITY_PATTERN.exec(identity);
  return match ? { userId: match[1] as string, sessionId: match[2] as string } : null;
}

/**
 * LiveKit identity of an outside guest's device in a meeting room (D-1027): `g_{callGuestId}`, one
 * `call_guests` row per admitted device per call. A guest never gets a `u_` identity, so a guest
 * cannot pass as a member by choosing a member's name: every participant list derives the
 * "External", "Registered" or "Presenter" tag from this prefix and the server-set label.
 */
export function liveKitGuestIdentity(callGuestId: string): string {
  return `g_${callGuestId}`;
}

const GUEST_IDENTITY_PATTERN = new RegExp(`^g_(${UUID})$`);

/** A member device (`u_{userId}_{sessionId}`) or an outside guest's device (`g_{callGuestId}`). */
export type LiveKitParticipantIdentity =
  { kind: 'member'; userId: string; sessionId: string } | { kind: 'guest'; callGuestId: string };

/**
 * Which kind of participant an identity names (D-1027): `member` for {@link liveKitIdentity},
 * `guest` for {@link liveKitGuestIdentity}, null for anything else. `parseLiveKitIdentity` keeps
 * answering for member identities only.
 */
export function parseLiveKitParticipantIdentity(
  identity: string,
): LiveKitParticipantIdentity | null {
  const member = parseLiveKitIdentity(identity);
  if (member) return { kind: 'member', ...member };
  const guest = GUEST_IDENTITY_PATTERN.exec(identity);
  return guest ? { kind: 'guest', callGuestId: guest[1] as string } : null;
}

// ---------------------------------------------------------------- limits

/**
 * The effective participant cap: the workspace setting, never above the 50-person hard ceiling.
 * A join that would pass it answers `409 call_full`. A webinar room has its own explicit cap
 * (`calls.participant_cap`, at most `CALL_LIMITS.webinarMaxParticipants`, D-1031).
 */
export function effectiveMaxParticipants(setting: number): number {
  return Math.max(2, Math.min(setting, CALL_LIMITS.maxParticipants));
}

/** A webinar room's cap: capacity plus presenters, within 2..510 (D-1031, D-1232). */
export function webinarRoomCap(capacity: number, presenters: number): number {
  return Math.max(2, Math.min(capacity + presenters, CALL_LIMITS.webinarMaxParticipants));
}
